import { TrashIcon, PaperclipIcon, XIcon } from '@phosphor-icons/react';
import { Form } from 'formik';
import { Formik } from 'components/form/formik';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useSWR, { mutate as globalMutate } from 'swr';
import { captureException } from '@sentry/react';

import { PageHeader } from '../../../components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { InputField } from '../../../components/input/InputField';
import { TextAreaField } from '../../../components/textarea/TextAreaField';
import { Button } from '../../../components/button/Button';
import { Dropzone } from '../../../components/Dropzone';
import { SupplierComboboxField } from '../../supplier/SupplierComboboxField';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { DelayedSpinner } from '../../../components/Spinner';
import { getDisplayError } from '../../../utils/get-display-error';
import { ErrorState } from 'components/error-state/ErrorState';
import { fetchClaim, updateClaim, deleteClaim, ClaimResponse, ClaimStatusType } from '../models';
import { useDownloadDocument } from '../../document/hooks/useDownloadDocument';
import { CLAIM_STATUS_OPTIONS, validateClaimForm } from '../constants';
import { handleClaimDocumentUpload } from '../hooks/useClaimDocumentUpload';

interface FormDocument {
  id: string;
  name: string;
  hash: string;
}

interface ClaimFormValues {
  counterParty: string;
  supplier: { id: number; name: string } | null;
  amount: string;
  status: { key: string; name: string } | null;
  info: string;
  attachments: FormDocument[];
}

function claimToFormValues(claim: ClaimResponse): ClaimFormValues {
  return {
    counterParty: claim.counterParty,
    supplier: claim.supplierId && claim.supplierName ? { id: claim.supplierId, name: claim.supplierName } : null,
    amount: (claim.amount / 100).toFixed(2),
    status: CLAIM_STATUS_OPTIONS.find((s) => s.key === claim.status) ?? CLAIM_STATUS_OPTIONS[0]!,
    info: claim.info,
    attachments: claim.documents.map((d) => ({
      id: d.documentId,
      name: d.name,
      hash: d.hash,
    })),
  };
}

const ClaimPage = () => {
  const navigate = useNavigate();
  const { claimId } = useParams<{ claimId: string }>();
  const { downloadFile, downloadingFiles } = useDownloadDocument();

  const {
    data: claim,
    error,
    isLoading,
    mutate,
  } = useSWR(claimId ? ['claim', claimId] : null, () => fetchClaim(claimId!));

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  if (error || !claim) {
    return (
      <div className="p-4">
        <ErrorState
          error={error}
          title="Claim kon niet worden geladen"
          description={error ? getDisplayError(error) : 'De gevraagde claim werd niet gevonden.'}
          onRetry={error ? () => mutate() : undefined}
          variant="section"
        />
      </div>
    );
  }

  const title = `Claim - ${claim.counterParty}`;

  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Claims',
              to: '/internal/finance/claims',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div className="flex gap-2">
            <ConfirmDialog
              triggerColor="danger"
              triggerText={<TrashIcon className="button-icon" />}
              title="Claim verwijderen?"
              submitText="Verwijder claim"
              description={<div>Ben je zeker dat je deze claim wilt verwijderen?</div>}
              onSubmit={async () => {
                try {
                  await deleteClaim(claim.id);
                  toast.success('Claim verwijderd');
                  navigate('..');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon claim niet verwijderen: ${getDisplayError(err)}`);
                }
              }}
            />
          </div>
        }
      />

      <div className="p-7">
        <Formik
          initialValues={claimToFormValues(claim)}
          enableReinitialize
          validate={validateClaimForm}
          onSubmit={async (values) => {
            try {
              const amountInCents = Math.round(parseFloat(values.amount) * 100);

              await updateClaim(claim.id, {
                counterParty: values.counterParty,
                supplierId: values.supplier ? values.supplier.id : null,
                amount: amountInCents,
                status: (values.status?.key as ClaimStatusType) ?? 'Open',
                info: values.info,
                documentIds: values.attachments.map((a) => a.id),
              });

              await globalMutate(['claim', claimId]);
              toast.success('Claim bijgewerkt');
            } catch (err) {
              captureException(err);
              toast.error(`Kon claim niet bijwerken: ${getDisplayError(err)}`);
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <div className="card flex max-w-[520px] flex-col gap-4 p-5">
                <InputField labelText="Tegenpartij" name="counterParty" type="text" />

                <SupplierComboboxField name="supplier" labelText="Onderaannemer" isNullable={true} />

                <InputField labelText="Bedrag (€)" name="amount" type="number" step="0.01" />

                <SimpleSelectField labelText="Status" name="status" items={CLAIM_STATUS_OPTIONS} />

                <TextAreaField labelText="Info" name="info" rows={5} />

                {/* Attachments */}
                <div>
                  <label className="label-text mb-1.5 block">Bijlagen</label>

                  <div className="mb-4">
                    <Dropzone
                      files={[]}
                      allowMultiple={true}
                      onUpload={(files: globalThis.File[]) => {
                        handleClaimDocumentUpload(files, values.attachments, setFieldValue);
                      }}
                      accept="application/pdf,.pdf,.png,.jpg,.jpeg"
                    />
                  </div>

                  {values.attachments.length > 0 && (
                    <div className="border border-border rounded-md p-3 mb-4">
                      <div className="mb-2 flex items-center text-xs font-medium text-fg-secondary">
                        <PaperclipIcon className="w-4 h-4 mr-1" />
                        Geüploade bijlagen ({values.attachments.length})
                      </div>

                      <div className="space-y-2">
                        {values.attachments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between bg-surface-sunken rounded p-2">
                            <div className="flex items-center flex-1">
                              <PaperclipIcon className="w-4 h-4 text-fg-muted mr-2" />
                              <span className="text-sm text-fg-secondary truncate">{doc.name}</span>
                            </div>

                            <div className="flex items-center space-x-2">
                              <Button
                                color="secondary"
                                onTrigger={() => downloadFile(doc)}
                                isLoading={downloadingFiles.includes(doc.id)}
                              >
                                Download
                              </Button>
                              <Button
                                color="danger"
                                onTrigger={() => {
                                  setFieldValue(
                                    'attachments',
                                    values.attachments.filter((a) => a.id !== doc.id),
                                  );
                                }}
                              >
                                <XIcon className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Button type="submit" color="primary" isDisabled={isSubmitting} isLoading={isSubmitting}>
                    Opslaan
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ClaimPage;
